import React, { useState } from 'react';
import { motion } from 'framer-motion';
import { useNavigate, Link } from 'react-router-dom';
import SafeIcon from '../../common/SafeIcon';
import * as FiIcons from 'react-icons/fi';
import { useAuth } from '../../contexts/AuthContext';

const { FiUser, FiLock, FiMail, FiBriefcase, FiArrowLeft } = FiIcons;

const Register = () => {
  const [formData, setFormData] = useState({
    name: '',
    email: '',
    password: '',
    company: ''
  });
  const [error, setError] = useState('');
  const [loading, setLoading] = useState(false);
  const { register } = useAuth();
  const navigate = useNavigate();

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);
    setError('');
    
    try {
      const result = await register(formData);
      if (result.success) {
        // Registration successful, Supabase usually logs the user in automatically
        // but we navigate to dashboard to be sure.
        navigate('/dashboard');
      } else {
        setError(result.error || 'Registration failed. Please try again.');
      }
    } catch (err) {
      setError('An unexpected error occurred.');
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen bg-slate-900 flex items-center justify-center p-4">
      <motion.div 
        initial={{ opacity: 0, scale: 0.95 }} 
        animate={{ opacity: 1, scale: 1 }} 
        className="bg-white rounded-3xl p-10 w-full max-w-md shadow-2xl border border-slate-100"
      >
        <div className="mb-8">
          <Link to="/login" className="text-slate-400 hover:text-blue-600 flex items-center gap-2 text-sm font-bold transition-colors">
            <SafeIcon icon={FiArrowLeft} /> Back to Login
          </Link>
        </div>

        <div className="text-center mb-10">
          <div className="w-16 h-16 bg-blue-600 rounded-2xl flex items-center justify-center mx-auto mb-4 shadow-xl shadow-blue-200">
            <SafeIcon icon={FiBriefcase} className="text-white text-2xl" />
          </div>
          <h1 className="text-3xl font-black text-slate-900 tracking-tighter">Join JINXos</h1>
          <p className="text-slate-400 font-medium">Create your business workspace</p>
        </div>

        <form onSubmit={handleSubmit} className="space-y-4">
          <div className="relative">
            <SafeIcon icon={FiUser} className="absolute left-4 top-1/2 -translate-y-1/2 text-slate-400" />
            <input 
              name="name" 
              placeholder="Full Name" 
              required 
              value={formData.name}
              onChange={e => setFormData({ ...formData, name: e.target.value })} 
              className="w-full pl-12 pr-4 py-4 bg-slate-50 rounded-2xl border border-slate-100 outline-none focus:ring-2 focus:ring-blue-500 transition-all" 
            />
          </div>

          <div className="relative">
            <SafeIcon icon={FiBriefcase} className="absolute left-4 top-1/2 -translate-y-1/2 text-slate-400" />
            <input 
              name="company" 
              placeholder="Company Name" 
              required 
              value={formData.company}
              onChange={e => setFormData({ ...formData, company: e.target.value })} 
              className="w-full pl-12 pr-4 py-4 bg-slate-50 rounded-2xl border border-slate-100 outline-none focus:ring-2 focus:ring-blue-500 transition-all" 
            />
          </div>

          <div className="relative">
            <SafeIcon icon={FiMail} className="absolute left-4 top-1/2 -translate-y-1/2 text-slate-400" />
            <input 
              name="email" 
              type="email" 
              placeholder="Email Address" 
              required 
              value={formData.email}
              onChange={e => setFormData({ ...formData, email: e.target.value })} 
              className="w-full pl-12 pr-4 py-4 bg-slate-50 rounded-2xl border border-slate-100 outline-none focus:ring-2 focus:ring-blue-500 transition-all" 
            />
          </div>

          <div className="relative">
            <SafeIcon icon={FiLock} className="absolute left-4 top-1/2 -translate-y-1/2 text-slate-400" />
            <input 
              name="password" 
              type="password" 
              placeholder="Secure Password" 
              required 
              value={formData.password}
              onChange={e => setFormData({ ...formData, password: e.target.value })} 
              className="w-full pl-12 pr-4 py-4 bg-slate-50 rounded-2xl border border-slate-100 outline-none focus:ring-2 focus:ring-blue-500 transition-all" 
            />
          </div>

          {error && (
            <motion.div 
              initial={{ opacity: 0, y: -10 }} 
              animate={{ opacity: 1, y: 0 }}
              className="bg-red-50 text-red-600 p-4 rounded-xl text-xs font-bold text-center border border-red-100"
            >
              {error}
            </motion.div>
          )}

          <button 
            type="submit" 
            disabled={loading} 
            className="w-full bg-blue-600 text-white py-4 rounded-2xl font-black shadow-xl shadow-blue-100 hover:bg-blue-700 transition-all disabled:opacity-50"
          >
            {loading ? 'Creating Account...' : 'Register Workspace'}
          </button>
        </form>
        
        <p className="text-center mt-6 text-slate-400 text-sm font-medium">
          Already have an account? <Link to="/login" className="text-blue-600 font-bold">Sign In</Link>
        </p>
      </motion.div>
    </div>
  );
};

export default Register;